import {
  type AuthError,
  createUserWithEmailAndPassword,
  getRedirectResult,
  GoogleAuthProvider,
  linkWithPopup,
  linkWithRedirect,
  onAuthStateChanged,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  User,
  type UserCredential,
} from 'firebase/auth';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';

import { UserProfile } from '@/types/user';

import {
  getAuthInstance,
  getDbInstance,
  getGoogleProviderInstance,
  isEmulatorEnabled,
} from './firebase';
import { rememberGuestUid, reportGuestIdentityLink } from './viewer-identity';

// メール・パスワードでログイン
export const signInWithEmail = async (email: string, password: string) => {
  try {
    const authInstance = getAuthInstance();
    const userCredential = await signInWithEmailAndPassword(authInstance, email, password);
    await updateUserLoginTime(userCredential.user.uid);
    return userCredential;
  } catch (error) {
    console.error('Email sign in error:', error);
    throw error;
  }
};

// メール・パスワードで新規登録
export const signUpWithEmail = async (email: string, password: string) => {
  try {
    const {beginCheckoutRegistration,linkCheckoutMeasurement}=await import('./commerce-purchase');
    await beginCheckoutRegistration();
    const authInstance = getAuthInstance();
    const userCredential = await createUserWithEmailAndPassword(authInstance, email, password);
    await createUserProfile(userCredential.user);
    void linkCheckoutMeasurement();
    return userCredential;
  } catch (error) {
    console.error('Email sign up error:', error);
    throw error;
  }
};

/**
 * A guest (anonymous sign-in from the live page) who signs in with Google keeps their uid: the
 * Google credential is linked to the anonymous account, so their comments and their purchase in
 * progress stay theirs. If that Google account already belongs to a member, the existing account
 * wins: we sign in to it and the anonymous session is dropped (decision 10).
 */
const googleCredentialForCurrentUser = async (authInstance: ReturnType<typeof getAuthInstance>, provider: GoogleAuthProvider): Promise<UserCredential> => {
  const current = authInstance.currentUser;
  if (!current?.isAnonymous) return signInWithPopup(authInstance, provider);
  try {
    return await linkWithPopup(current, provider);
  } catch (error) {
    const code = (error as AuthError)?.code;
    if (code !== 'auth/credential-already-in-use' && code !== 'auth/email-already-in-use') throw error;
    const credential = GoogleAuthProvider.credentialFromError(error as AuthError);
    if (!credential) throw error;
    // ここで匿名 uid が捨てられる（決定 10）。捨てる前に覚えておき、ログインが済んでから
    // 控えを送る。そうしないと、同じ人の視聴と購入が 2 人分として数えられる。
    rememberGuestUid(current.uid);
    return signInWithCredential(authInstance, credential);
  }
};

// Google OAuth ログイン
export const signInWithGoogle = async () => {
  try {
    const authInstance = getAuthInstance();
    const provider = getGoogleProviderInstance();
    const dbInstance = getDbInstance();
    const isEmulator = isEmulatorEnabled();

    // エミュレーター環境では、まずリダイレクト結果を確認（リダイレクト後のコールバック時）
    if (isEmulator) {
      const redirectResult = await getRedirectResult(authInstance);
      if (redirectResult?.user) {
        const user = redirectResult.user;

        // ユーザープロフィールの確認・作成
        const userDoc = await getDoc(doc(dbInstance, 'users', user.uid));

        if (!userDoc?.exists()) {
          await createUserProfile(user, 'google');
        } else {
          await updateUserLoginTime(user.uid);
        }

        return redirectResult;
      }

      // リダイレクト結果がない場合、リダイレクトを開始
      // エミュレーターの認証ハンドラー（http://127.0.0.1:9099/emulator/auth/handler）でユーザーを作成できる
      if (authInstance.currentUser?.isAnonymous) {
        await linkWithRedirect(authInstance.currentUser, provider);
      } else {
        await signInWithRedirect(authInstance, provider);
      }
      // リダイレクトが開始されたので、ここで処理は終了（ページ遷移する）
      return null;
    }

    // 本番環境: ポップアップを試す（失敗した場合はリダイレクトにフォールバック）
    try {
      const userCredential = await googleCredentialForCurrentUser(authInstance, provider);
      const user = userCredential.user;
      // 匿名 uid を覚えていたときだけ、会員 uid との対応を控える。
      void reportGuestIdentityLink(user.uid);

      // ユーザープロフィールの確認・作成
      const userDoc = await getDoc(doc(dbInstance, 'users', user.uid));

      if (!userDoc?.exists()) {
        await createUserProfile(user, 'google');
      } else {
        await updateUserLoginTime(user.uid);
      }

      return userCredential;
    } catch (popupError) {
      // ポップアップが失敗した場合（ブロックされた、その他）
      const errorMessage = popupError instanceof Error ? popupError.message : String(popupError);

      // ポップアップがブロックされた場合のみリダイレクトにフォールバック
      if (
        errorMessage.includes('popup-blocked') ||
        errorMessage.includes('popup-closed-by-user') ||
        errorMessage.includes('auth/popup-blocked') ||
        errorMessage.includes('auth/popup-closed-by-user')
      ) {
        console.warn('ポップアップ認証が失敗しました。リダイレクトに切り替えます:', errorMessage);
        await signInWithRedirect(authInstance, provider);
        return null;
      }

      // その他のエラーはそのまま再スロー
      throw popupError;
    }
  } catch (error) {
    console.error('Google sign in error:', error);
    throw error;
  }
};

// ログアウト
export const signOutUser = async () => {
  try {
    const authInstance = getAuthInstance();
    await signOut(authInstance);
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
};

// ユーザープロフィール作成
const createUserProfile = async (user: User, provider: 'email' | 'google' = 'email') => {
  const dbInstance = getDbInstance();
  const userProfile: Omit<UserProfile, 'profile' | 'shippingAddress'> = {
    uid: user.uid,
    email: user.email!,
    displayName: user.displayName || undefined,
    photoURL: user.photoURL || undefined,
    provider,
    emailVerified: user.emailVerified,
    createdAt: Timestamp.now(),
    lastLoginAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };

  await setDoc(doc(dbInstance, 'users', user.uid), userProfile);
};

// ログイン時刻更新
const updateUserLoginTime = async (uid: string) => {
  const dbInstance = getDbInstance();
  const userRef = doc(dbInstance, 'users', uid);
  await setDoc(
    userRef,
    {
      lastLoginAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    },
    { merge: true }
  );
};

// 認証状態監視
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  const authInstance = getAuthInstance();
  return onAuthStateChanged(authInstance, callback);
};
